import React, { Suspense } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuthStore } from '../stores/auth.store';
import { loginPathFor } from '../utils/loginRedirect';
import Layout from './Layout';
import RouteLoadingFallback from './RouteLoadingFallback';

interface ProtectedLayoutProps {
  onThemeToggle: () => void;
}

/**
 * Parent route for every signed-in page: one auth check and one shell. Child
 * routes render into the `<Outlet />`, so the sidebar (organization picker,
 * open sections, collapsed rail) stays mounted across navigation; per-page
 * permission gates stay on the child routes (`AuthRoute`).
 *
 * While a stored session is still being restored nothing redirects; a signed-out
 * visitor goes to the login page with the requested page in `?next=`. The single
 * Suspense boundary (with router transitions) keeps the current page on screen
 * while the next page's code loads.
 */
const ProtectedLayout: React.FC<ProtectedLayoutProps> = ({ onThemeToggle }) => {
  const { isAuthenticated, authReady } = useAuthStore();
  const location = useLocation();

  if (!authReady) return <RouteLoadingFallback />;
  if (!isAuthenticated) {
    return <Navigate to={loginPathFor(`${location.pathname}${location.search}${location.hash}`)} replace />;
  }
  return (
    <Layout onThemeToggle={onThemeToggle}>
      <Suspense fallback={<RouteLoadingFallback />}>
        <Outlet />
      </Suspense>
    </Layout>
  );
};

export default ProtectedLayout;
